import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NAV_LINKS } from "@/lib/content";
import Logo from "@/components/Logo";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = () => navigate("/controlla-preventivo");

  return (
    <header className="sticky top-0 z-50 bg-[#F7F9FC]/80 backdrop-blur-xl border-b border-slate-200/70">
      <nav className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center"><Logo className="text-2xl" /></Link>

        <div className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.to.replace(/\//g, "")}`}
              className={`text-sm font-medium transition-colors ${location.pathname === l.to ? "text-[#3157F6]" : "text-slate-500 hover:text-[#3157F6]"}`}>
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button data-testid="nav-cta" onClick={go}
            className="hidden sm:inline-flex rounded-xl h-11 px-5 text-sm font-semibold bg-[#3157F6] hover:bg-[#2444D8] active:scale-95 transition-transform">
            Controlla il tuo preventivo
          </Button>
          <button data-testid="nav-menu-toggle" onClick={() => setOpen(true)}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100" aria-label="Apri menu">
            <Menu className="w-5 h-5 text-slate-800" />
          </button>
        </div>
      </nav>

      {createPortal(
        <AnimatePresence>
          {open && (
            <>
              {/* Overlay scuro */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }} onClick={() => setOpen(false)}
                className="fixed inset-0 z-[90] bg-[#0F172A]/45 lg:hidden" data-testid="mobile-menu-overlay" />

              {/* Pannello opaco slide-in da destra */}
              <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="fixed top-0 right-0 bottom-0 z-[100] w-[85%] max-w-sm bg-[#F7F9FC] shadow-2xl flex flex-col lg:hidden"
                data-testid="mobile-menu">
                <div className="px-5 h-16 flex items-center justify-between border-b border-[#E2E8F0] flex-shrink-0 bg-[#F7F9FC]">
                  <Logo className="text-2xl" />
                  <button data-testid="mobile-menu-close" onClick={() => setOpen(false)}
                    className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-[#E2E8F0]" aria-label="Chiudi menu">
                    <X className="w-6 h-6 text-[#0F172A]" strokeWidth={2} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col">
                  {NAV_LINKS.map((l) => (
                    <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.to.replace(/\//g, "")}`}
                      className="block py-4 text-lg font-heading font-semibold text-[#0F172A] border-b border-[#E2E8F0]">
                      {l.label}
                    </Link>
                  ))}
                  <Button data-testid="mobile-nav-cta" onClick={go}
                    className="mt-6 w-full rounded-xl h-14 text-base font-semibold bg-[#3157F6] hover:bg-[#2444D8] text-white">
                    Controlla il mio preventivo <ArrowRight className="w-5 h-5 ml-1" />
                  </Button>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
